import type { TruthTableExercise } from "../components/TruthTableBuilder";

const COMMON_INTERVENTIONS = [
  { id: "email", label: "Email Alert", type: "email" as const, description: "Send email notification to monitoring team" },
  { id: "sms", label: "SMS Text Alert", type: "sms" as const, description: "Send SMS to on-call personnel" },
  { id: "alert", label: "Automated Alert", type: "alert" as const, description: "System-wide automated alert broadcast" },
  { id: "human", label: "Human Dispatch", type: "human" as const, description: "Dispatch human responder to investigate" },
  { id: "hardware", label: "Hardware Shutdown", type: "hardware" as const, description: "Automated hardware system shutdown" },
  { id: "software", label: "Software Intervention", type: "software" as const, description: "Automated software process adjustment" },
  { id: "report", label: "Generate Report", type: "report" as const, description: "Log incident and generate compliance report" },
  { id: "evacuate", label: "Evacuation Order", type: "evacuate" as const, description: "Trigger evacuation protocol" },
];

function buildCorrectInterventions(var1States: string[], var2States: string[], var3States: string[] | null): Record<string, string> {
  const result: Record<string, string> = {};
  for (const s1 of var1States) {
    for (const s2 of var2States) {
      if (var3States) {
        for (const s3 of var3States) {
          const key = `${s1}|${s2}|${s3}`;
          const critical = s1.includes("critical") || s1.includes("fail") || s1.includes("high") || s2.includes("critical") || s2.includes("fail") || s2.includes("high") || s3.includes("critical") || s3.includes("fail") || s3.includes("high");
          const warning = s1.includes("warning") || s1.includes("elevated") || s2.includes("warning") || s2.includes("elevated") || s3.includes("warning") || s3.includes("elevated");
          if (critical) result[key] = "evacuate";
          else if (warning) result[key] = "human";
          else result[key] = "report";
        }
      } else {
        const key = `${s1}|${s2}`;
        const critical = s1.includes("critical") || s1.includes("fail") || s1.includes("high") || s2.includes("critical") || s2.includes("fail") || s2.includes("high");
        const warning = s1.includes("warning") || s1.includes("elevated") || s2.includes("warning") || s2.includes("elevated");
        if (critical) result[key] = "hardware";
        else if (warning) result[key] = "sms";
        else result[key] = "report";
      }
    }
  }
  return result;
}

function makeExercise(industry: string, var1: { domain: string; label: string; description: string; states: { value: string; label: string; description: string }[] }, var2: { domain: string; label: string; description: string; states: { value: string; label: string; description: string }[] }, hint: string): TruthTableExercise {
  return {
    id: `tt-${industry.toLowerCase().replace(/[^a-z0-9]/g, "-")}`,
    title: `Decision Logic Exercise: ${industry}`,
    description: `In this exercise, you'll build a truth table for ${industry} safety monitoring. Select 2-3 sensor domains, then assign the appropriate Sentinel intervention for each combination of sensor states. This teaches you how to design autonomous decision logic that determines what action to take based on real-time sensor data.`,
    industry,
    variables: [{ ...var1, id: "var-1" }, { ...var2, id: "var-2" }],
    interventions: COMMON_INTERVENTIONS,
    correctInterventions: buildCorrectInterventions(
      var1.states.map((s) => s.value),
      var2.states.map((s) => s.value),
      null,
    ),
    hint,
  };
}

export const TRUTH_TABLE_EXERCISES: Record<string, TruthTableExercise> = {
  "Public Safety": makeExercise(
    "Public Safety",
    { domain: "Crowd Sensors", label: "Crowd Density", description: "Real-time crowd density monitoring from camera and sensor fusion", states: [
      { value: "normal", label: "Normal", description: "Crowd density within safe limits" },
      { value: "elevated", label: "Elevated", description: "Crowd density approaching capacity" },
      { value: "critical", label: "Critical", description: "Crowd density exceeds safe threshold" },
    ]},
    { domain: "Infrastructure", label: "Structural Integrity", description: "Bridge and platform structural sensor data", states: [
      { value: "stable", label: "Stable", description: "All structural sensors nominal" },
      { value: "stress", label: "Stress Detected", description: "Stress patterns emerging in structure" },
      { value: "fail", label: "Failure Imminent", description: "Structural failure predicted" },
    ]},
    "When crowd density is critical AND structural integrity is compromised, an evacuation is needed. When either is elevated but not critical, dispatch human responders. When both are normal, just log and report.",
  ),

  "School Safety": makeExercise(
    "School Safety",
    { domain: "Access Control", label: "Perimeter Security", description: "Door and gate access control sensor data", states: [
      { value: "secure", label: "Secure", description: "All access points secured" },
      { value: "breach", label: "Breach Attempt", description: "Unauthorized access detected" },
      { value: "critical", label: "Active Breach", description: "Perimeter breached — intruder on campus" },
    ]},
    { domain: "Environmental", label: "Air Quality", description: "HVAA and air quality sensor readings", states: [
      { value: "normal", label: "Normal", description: "Air quality within safe range" },
      { value: "warning", label: "Warning", description: "Air quality degrading — possible hazard" },
      { value: "critical", label: "Critical", description: "Hazardous air quality — health risk" },
    ]},
    "An active perimeter breach with hazardous air quality requires immediate evacuation. A breach attempt with normal air quality needs human dispatch. Normal conditions just need logging.",
  ),

  "Municipal Safety": makeExercise(
    "Municipal Safety",
    { domain: "Traffic", label: "Traffic Flow", description: "Real-time traffic sensor and signal data", states: [
      { value: "flow", label: "Flowing", description: "Traffic flowing normally" },
      { value: "congested", label: "Congested", description: "Traffic congestion building" },
      { value: "gridlock", label: "Gridlock", description: "Complete traffic breakdown" },
    ]},
    { domain: "Public Works", label: "Infrastructure Status", description: "Road, bridge, and utility sensor data", states: [
      { value: "operational", label: "Operational", description: "All infrastructure systems operational" },
      { value: "degraded", label: "Degraded", description: "Infrastructure showing wear or issues" },
      { value: "fail", label: "Failure", description: "Infrastructure failure — road/bridge/utility" },
    ]},
    "Gridlock traffic with infrastructure failure requires emergency hardware intervention. Congested traffic with degraded infrastructure needs human dispatch. Normal conditions need reporting only.",
  ),

  "Emergency Services": makeExercise(
    "Emergency Services",
    { domain: "Fire Sensors", label: "Fire Detection", description: "Smoke, heat, and gas sensor readings", states: [
      { value: "clear", label: "Clear", description: "No fire indicators detected" },
      { value: "smoke", label: "Smoke Detected", description: "Smoke particles detected — pre-ignition" },
      { value: "active", label: "Active Fire", description: "Fire confirmed — thermal threshold breached" },
    ]},
    { domain: "EMS", label: "Casualty Load", description: "Medical emergency and casualty sensor data", states: [
      { value: "none", label: "None", description: "No medical emergencies" },
      { value: "minor", label: "Minor", description: "Minor injuries reported" },
      { value: "mass", label: "Mass Casualty", description: "Mass casualty event detected" },
    ]},
    "An active fire with mass casualties requires evacuation. Smoke detection with minor injuries needs human dispatch. Clear conditions with no casualties just need reporting.",
  ),

  "Senior Care": makeExercise(
    "Senior Care",
    { domain: "Wearable", label: "Vital Signs", description: "Resident wearable vital sign monitoring", states: [
      { value: "stable", label: "Stable", description: "Vital signs within normal range" },
      { value: "declining", label: "Declining", description: "Vital signs showing decline trend" },
      { value: "critical", label: "Critical", description: "Vital signs critical — immediate attention" },
    ]},
    { domain: "Motion", label: "Fall Detection", description: "Accelerometer and motion sensor data", states: [
      { value: "normal", label: "Normal Movement", description: "Normal activity patterns" },
      { value: "irregular", label: "Irregular Movement", description: "Unusual movement — possible fall risk" },
      { value: "fall", label: "Fall Detected", description: "Fall confirmed by sensor data" },
    ]},
    "Critical vitals with a confirmed fall requires immediate human dispatch. Declining vitals with irregular movement needs an SMS alert. Stable vitals with normal movement just needs logging.",
  ),

  "Public Transit": makeExercise(
    "Public Transit",
    { domain: "Vehicle Health", label: "Fleet Condition", description: "Vehicle diagnostic and telematics data", states: [
      { value: "healthy", label: "Healthy", description: "All vehicle systems nominal" },
      { value: "warning", label: "Warning", description: "Vehicle showing wear — maintenance needed" },
      { value: "critical", label: "Critical", description: "Vehicle failure imminent — safety risk" },
    ]},
    { domain: "Track/Road", label: "Infrastructure Condition", description: "Track, road, and signal sensor data", states: [
      { value: "good", label: "Good", description: "Infrastructure in good condition" },
      { value: "degraded", label: "Degraded", description: "Infrastructure showing wear" },
      { value: "fail", label: "Failure", description: "Infrastructure failure detected" },
    ]},
    "A critical vehicle condition with infrastructure failure requires hardware shutdown. Warning vehicle condition with degraded infrastructure needs human dispatch. Good conditions need reporting.",
  ),

  "Food Safety": makeExercise(
    "Food Safety",
    { domain: "Temperature", label: "Cold Chain", description: "Storage and transport temperature sensors", states: [
      { value: "optimal", label: "Optimal", description: "Temperature within safe range" },
      { value: "deviation", label: "Deviation", description: "Temperature drifting from safe range" },
      { value: "breach", label: "Breach", description: "Cold chain broken — food safety risk" },
    ]},
    { domain: "Contamination", label: "Pathogen Detection", description: "Contamination sensor and testing data", states: [
      { value: "clean", label: "Clean", description: "No contamination detected" },
      { value: "suspect", label: "Suspect", description: "Possible contamination — needs verification" },
      { value: "confirmed", label: "Confirmed", description: "Contamination confirmed — health hazard" },
    ]},
    "A cold chain breach with confirmed contamination requires immediate hardware shutdown and evacuation. A temperature deviation with suspect contamination needs human dispatch. Optimal conditions need reporting.",
  ),

  "Agriculture": makeExercise(
    "Agriculture",
    { domain: "Soil", label: "Soil Health", description: "Soil moisture, pH, and nutrient sensors", states: [
      { value: "healthy", label: "Healthy", description: "Soil conditions optimal for crops" },
      { value: "degraded", label: "Degraded", description: "Soil showing nutrient or moisture stress" },
      { value: "critical", label: "Critical", description: "Soil conditions critical — crop failure risk" },
    ]},
    { domain: "Equipment", label: "Machinery Status", description: "Tractor and irrigation equipment sensors", states: [
      { value: "operational", label: "Operational", description: "All equipment functioning normally" },
      { value: "maintenance", label: "Maintenance Needed", description: "Equipment showing wear — service required" },
      { value: "fail", label: "Failure", description: "Equipment failure — operation halted" },
    ]},
    "Critical soil conditions with equipment failure requires hardware shutdown. Degraded soil with maintenance-needed equipment needs human dispatch. Healthy conditions need reporting.",
  ),

  "Environmental": makeExercise(
    "Environmental",
    { domain: "Air Quality", label: "AQI Monitoring", description: "Air quality index sensor network", states: [
      { value: "good", label: "Good (0-50)", description: "Air quality is satisfactory" },
      { value: "moderate", label: "Moderate (51-100)", description: "Air quality acceptable but some concern" },
      { value: "hazardous", label: "Hazardous (200+)", description: "Health alert — everyone may be affected" },
    ]},
    { domain: "Water Quality", label: "Water Contamination", description: "Water source and treatment sensor data", states: [
      { value: "safe", label: "Safe", description: "Water quality within safe limits" },
      { value: "elevated", label: "Elevated", description: "Contamination indicators above normal" },
      { value: "contaminated", label: "Contaminated", description: "Water contamination confirmed — health risk" },
    ]},
    "Hazardous air with contaminated water requires evacuation. Moderate air with elevated water contamination needs human dispatch. Good air with safe water needs reporting.",
  ),

  "Water Utilities": makeExercise(
    "Water Utilities",
    { domain: "Pressure", label: "Pipe Pressure", description: "Distribution pipe pressure sensors", states: [
      { value: "normal", label: "Normal", description: "Pressure within operational range" },
      { value: "low", label: "Low Pressure", description: "Pressure dropping — possible leak" },
      { value: "critical", label: "Critical Loss", description: "Pressure critical — pipe failure" },
    ]},
    { domain: "Quality", label: "Treatment Quality", description: "Treatment plant output quality sensors", states: [
      { value: "treated", label: "Treated", description: "Water fully treated — safe for distribution" },
      { value: "drifting", label: "Drifting", description: "Treatment quality drifting from spec" },
      { value: "fail", label: "Treatment Failure", description: "Treatment process failure — unsafe water" },
    ]},
    "Critical pressure loss with treatment failure requires hardware shutdown. Low pressure with drifting quality needs human dispatch. Normal pressure with treated water needs reporting.",
  ),

  "Chemical Safety": makeExercise(
    "Chemical Safety",
    { domain: "Gas Sensors", label: "Gas Concentration", description: "Chemical gas detection sensor network", states: [
      { value: "clear", label: "Clear", description: "No hazardous gas detected" },
      { value: "trace", label: "Trace Detected", description: "Trace amounts — monitor closely" },
      { value: "leak", label: "Leak Confirmed", description: "Chemical leak — exposure risk" },
    ]},
    { domain: "Ventilation", label: "Ventilation Status", description: "Facility ventilation and filtration systems", states: [
      { value: "operational", label: "Operational", description: "Ventilation running at full capacity" },
      { value: "reduced", label: "Reduced", description: "Ventilation operating at reduced capacity" },
      { value: "fail", label: "Failed", description: "Ventilation system failure" },
    ]},
    "A confirmed chemical leak with ventilation failure requires immediate evacuation. Trace gas with reduced ventilation needs human dispatch. Clear conditions need reporting.",
  ),

  "Waste Management": makeExercise(
    "Waste Management",
    { domain: "Gas Buildup", label: "Methane Levels", description: "Landfill methane and gas sensors", states: [
      { value: "normal", label: "Normal", description: "Gas levels within safe range" },
      { value: "elevated", label: "Elevated", description: "Gas buildup increasing — monitor" },
      { value: "critical", label: "Critical", description: "Gas at explosive levels" },
    ]},
    { domain: "Leachate", label: "Leachate Containment", description: "Leachate collection and containment sensors", states: [
      { value: "contained", label: "Contained", description: "Leachate fully contained" },
      { value: "seepage", label: "Seepage Detected", description: "Leachate seepage — containment breach risk" },
      { value: "breach", label: "Breach", description: "Leachate containment breached — environmental hazard" },
    ]},
    "Critical gas levels with leachate breach requires evacuation. Elevated gas with seepage needs human dispatch. Normal conditions need reporting.",
  ),

  "Manufacturing": makeExercise(
    "Manufacturing",
    { domain: "Machine", label: "Equipment Vibration", description: "Production line vibration and temperature sensors", states: [
      { value: "normal", label: "Normal", description: "All machines operating within spec" },
      { value: "elevated", label: "Elevated", description: "Vibration increasing — wear detected" },
      { value: "critical", label: "Critical", description: "Machine failure imminent" },
    ]},
    { domain: "Quality", label: "Quality Deviation", description: "Production quality sensor and inspection data", states: [
      { value: "in_spec", label: "In Spec", description: "Quality within acceptable parameters" },
      { value: "drifting", label: "Drifting", description: "Quality drifting from spec — investigate" },
      { value: "defect", label: "Defect Cascade", description: "Quality defects cascading — line issue" },
    ]},
    "Critical machine condition with defect cascade requires hardware shutdown. Elevated vibration with drifting quality needs human dispatch. Normal conditions need reporting.",
  ),

  "Mechanical Systems": makeExercise(
    "Mechanical Systems",
    { domain: "Vibration", label: "Bearing Vibration", description: "Bearing and shaft vibration sensor data", states: [
      { value: "smooth", label: "Smooth", description: "Vibration within normal range" },
      { value: "rough", label: "Rough", description: "Vibration increasing — bearing wear" },
      { value: "fail", label: "Failure", description: "Bearing failure detected" },
    ]},
    { domain: "Temperature", label: "Operating Temperature", description: "System operating temperature sensors", states: [
      { value: "normal", label: "Normal", description: "Temperature within operational range" },
      { value: "high", label: "High", description: "Temperature elevated — cooling issue" },
      { value: "critical", label: "Critical", description: "Temperature critical — thermal failure" },
    ]},
    "Bearing failure with critical temperature requires hardware shutdown. Rough vibration with high temperature needs human dispatch. Smooth operation with normal temperature needs reporting.",
  ),

  "Fire Safety": makeExercise(
    "Fire Safety",
    { domain: "Smoke", label: "Smoke Detection", description: "Multi-zone smoke detector readings", states: [
      { value: "clear", label: "Clear", description: "No smoke detected" },
      { value: "trace", label: "Trace Smoke", description: "Trace smoke — pre-ignition indicator" },
      { value: "active", label: "Active Smoke", description: "Smoke confirmed — fire likely" },
    ]},
    { domain: "Heat", label: "Thermal Sensors", description: "Heat and thermal imaging sensor data", states: [
      { value: "normal", label: "Normal", description: "Temperature within normal range" },
      { value: "rising", label: "Rising", description: "Temperature rising rapidly" },
      { value: "ignition", label: "Ignition", description: "Thermal threshold breached — fire confirmed" },
    ]},
    "Active smoke with thermal ignition requires evacuation. Trace smoke with rising temperature needs human dispatch. Clear conditions need reporting.",
  ),

  "Construction": makeExercise(
    "Construction",
    { domain: "Structural", label: "Structural Stress", description: "Structural load and stress sensors", states: [
      { value: "stable", label: "Stable", description: "Structural loads within design limits" },
      { value: "stress", label: "Stress", description: "Stress accumulating — monitor" },
      { value: "critical", label: "Critical", description: "Structural failure imminent" },
    ]},
    { domain: "Weather", label: "Weather Conditions", description: "Wind, rain, and weather sensor data", states: [
      { value: "favorable", label: "Favorable", description: "Weather conditions safe for work" },
      { value: "adverse", label: "Adverse", description: "Weather deteriorating — caution" },
      { value: "severe", label: "Severe", description: "Severe weather — stop work" },
    ]},
    "Critical structural stress with severe weather requires evacuation. Stress with adverse weather needs human dispatch. Stable conditions need reporting.",
  ),

  "Mining": makeExercise(
    "Mining",
    { domain: "Gas", label: "Mine Gas Levels", description: "Underground gas detection sensors", states: [
      { value: "safe", label: "Safe", description: "Gas levels within safe limits" },
      { value: "rising", label: "Rising", description: "Gas levels increasing — ventilation check" },
      { value: "explosive", label: "Explosive", description: "Gas at explosive concentration" },
    ]},
    { domain: "Ground", label: "Ground Stability", description: "Geophone and ground stress sensors", states: [
      { value: "stable", label: "Stable", description: "Ground conditions stable" },
      { value: "shifting", label: "Shifting", description: "Ground movement detected — monitor" },
      { value: "collapse", label: "Collapse Risk", description: "Cave-in risk — evacuate immediately" },
    ]},
    "Explosive gas with collapse risk requires evacuation. Rising gas with shifting ground needs human dispatch. Safe conditions need reporting.",
  ),

  "Oil & Gas": makeExercise(
    "Oil & Gas",
    { domain: "Pressure", label: "Pipeline Pressure", description: "Pipeline pressure and flow sensors", states: [
      { value: "normal", label: "Normal", description: "Pressure within operational parameters" },
      { value: "fluctuating", label: "Fluctuating", description: "Pressure fluctuations — investigate" },
      { value: "rupture", label: "Rupture Risk", description: "Pressure critical — rupture imminent" },
    ]},
    { domain: "Gas Detection", label: "H2S / Gas", description: "Hydrogen sulfide and gas leak sensors", states: [
      { value: "clear", label: "Clear", description: "No gas leaks detected" },
      { value: "trace", label: "Trace", description: "Trace gas — monitor closely" },
      { value: "leak", label: "Leak", description: "Gas leak confirmed — explosion risk" },
    ]},
    "Rupture risk with confirmed gas leak requires evacuation. Fluctuating pressure with trace gas needs human dispatch. Normal conditions need reporting.",
  ),

  "Automotive": makeExercise(
    "Automotive",
    { domain: "ADAS", label: "Sensor Calibration", description: "ADAS sensor calibration and health data", states: [
      { value: "calibrated", label: "Calibrated", description: "All ADAS sensors within calibration" },
      { value: "drifting", label: "Drifting", description: "Sensor drift detected — recalibrate" },
      { value: "fail", label: "Failure", description: "ADAS sensor failure — safety risk" },
    ]},
    { domain: "Collision", label: "Collision Risk", description: "Forward collision warning sensor data", states: [
      { value: "clear", label: "Clear", description: "No collision risk detected" },
      { value: "approaching", label: "Approaching", description: "Collision risk increasing" },
      { value: "imminent", label: "Imminent", description: "Collision imminent — brake now" },
    ]},
    "ADAS failure with imminent collision requires hardware intervention (emergency braking). Drifting sensors with approaching risk needs human alert. Clear conditions need reporting.",
  ),

  "Aviation": makeExercise(
    "Aviation",
    { domain: "Engine", label: "Engine Vibration", description: "Engine vibration and temperature sensors", states: [
      { value: "smooth", label: "Smooth", description: "Engine operating within parameters" },
      { value: "rough", label: "Rough", description: "Engine vibration elevated — investigate" },
      { value: "fail", label: "Failure", description: "Engine failure detected" },
    ]},
    { domain: "Avionics", label: "Avionics Bus", description: "Avionics bus health and redundancy status", states: [
      { value: "nominal", label: "Nominal", description: "All avionics systems nominal" },
      { value: "degraded", label: "Degraded", description: "Avionics degraded — backup active" },
      { value: "fail", label: "Failed", description: "Avionics failure — emergency" },
    ]},
    "Engine failure with avionics failure requires immediate evacuation protocol. Rough engine with degraded avionics needs human (pilot) intervention. Smooth engine with nominal avionics needs reporting.",
  ),

  "Rail Transit": makeExercise(
    "Rail Transit",
    { domain: "Signal", label: "Signal System", description: "Trackside signal and communication sensors", states: [
      { value: "clear", label: "Clear", description: "All signals operating normally" },
      { value: "intermittent", label: "Intermittent", description: "Signal issues — investigate" },
      { value: "fail", label: "Failure", description: "Signal system failure — stop traffic" },
    ]},
    { domain: "Track", label: "Track Integrity", description: "Track geometry and integrity sensors", states: [
      { value: "good", label: "Good", description: "Track geometry within spec" },
      { value: "worn", label: "Worn", description: "Track wear detected — speed restriction" },
      { value: "fail", label: "Failure", description: "Track defect — derailment risk" },
    ]},
    "Signal failure with track failure requires hardware shutdown (emergency stop). Intermittent signals with worn track needs human dispatch. Clear signals with good track needs reporting.",
  ),

  "Maritime": makeExercise(
    "Maritime",
    { domain: "Propulsion", label: "Propulsion Health", description: "Propulsion system vibration and temperature", states: [
      { value: "normal", label: "Normal", description: "Propulsion systems operating normally" },
      { value: "warning", label: "Warning", description: "Propulsion anomalies — investigate" },
      { value: "fail", label: "Failure", description: "Propulsion failure — maneuvering limited" },
    ]},
    { domain: "Hull", label: "Hull Stress", description: "Hull stress and structural sensors", states: [
      { value: "nominal", label: "Nominal", description: "Hull stress within design limits" },
      { value: "elevated", label: "Elevated", description: "Hull stress elevated — reduce speed" },
      { value: "critical", label: "Critical", description: "Hull stress critical — structural risk" },
    ]},
    "Propulsion failure with critical hull stress requires evacuation. Warning propulsion with elevated hull stress needs human dispatch. Normal conditions need reporting.",
  ),

  "Space Systems": makeExercise(
    "Space Systems",
    { domain: "Telemetry", label: "Component Health", description: "Spacecraft component telemetry streams", states: [
      { value: "nominal", label: "Nominal", description: "All components operating nominally" },
      { value: "anomaly", label: "Anomaly", description: "Component anomaly detected — investigate" },
      { value: "fail", label: "Failure", description: "Component failure — reconfigure" },
    ]},
    { domain: "Thermal", label: "Thermal Control", description: "Thermal management system sensors", states: [
      { value: "stable", label: "Stable", description: "Thermal control within operational range" },
      { value: "drifting", label: "Drifting", description: "Thermal drifting — adjust cooling" },
      { value: "excursion", label: "Excursion", description: "Thermal excursion — component damage risk" },
    ]},
    "Component failure with thermal excursion requires software intervention (system reconfiguration). Anomaly with drifting thermal needs human (mission control) alert. Nominal conditions need reporting.",
  ),

  "Defense": makeExercise(
    "Defense",
    { domain: "Platform", label: "Platform Status", description: "Defense platform health and diagnostic sensors", states: [
      { value: "operational", label: "Operational", description: "Platform fully operational" },
      { value: "degraded", label: "Degraded", description: "Platform degraded — limited capability" },
      { value: "critical", label: "Critical", description: "Platform critical — protective action" },
    ]},
    { domain: "Threat", label: "Threat Assessment", description: "Multi-domain threat detection and scoring", states: [
      { value: "none", label: "No Threat", description: "No threats detected" },
      { value: "emerging", label: "Emerging", description: "Emerging threat — assess" },
      { value: "imminent", label: "Imminent", description: "Imminent threat — engage" },
    ]},
    "Critical platform status with imminent threat requires hardware intervention (protective maneuver). Degraded platform with emerging threat needs human dispatch. Operational platform with no threat needs reporting.",
  ),

  "Cybersecurity": makeExercise(
    "Cybersecurity",
    { domain: "Network", label: "Network Traffic", description: "Network traffic analysis and anomaly detection", states: [
      { value: "normal", label: "Normal", description: "Network traffic within baseline" },
      { value: "anomalous", label: "Anomalous", description: "Traffic anomalies detected — investigate" },
      { value: "breach", label: "Breach", description: "Network breach confirmed — isolate" },
    ]},
    { domain: "AI Systems", label: "AI Model Health", description: "AI model performance and drift monitoring", states: [
      { value: "stable", label: "Stable", description: "AI models performing within spec" },
      { value: "drifting", label: "Drifting", description: "Model drift detected — retrain" },
      { value: "compromised", label: "Compromised", description: "AI system compromised — adversarial attack" },
    ]},
    "Network breach with compromised AI requires hardware intervention (system isolation). Anomalous traffic with drifting models needs human dispatch. Normal traffic with stable models needs reporting.",
  ),

  "Critical Infrastructure": makeExercise(
    "Critical Infrastructure",
    { domain: "Grid", label: "Power Grid", description: "Power grid frequency and load sensors", states: [
      { value: "stable", label: "Stable", description: "Grid frequency and load stable" },
      { value: "fluctuating", label: "Fluctuating", description: "Grid fluctuations — investigate" },
      { value: "fail", label: "Cascade Risk", description: "Grid cascade failure risk" },
    ]},
    { domain: "Physical Security", label: "Perimeter Security", description: "Physical security and access control", states: [
      { value: "secure", label: "Secure", description: "All perimeter security nominal" },
      { value: "breach", label: "Breach Attempt", description: "Physical breach attempt detected" },
      { value: "penetrated", label: "Penetrated", description: "Perimeter penetrated — facility at risk" },
    ]},
    "Grid cascade risk with penetrated perimeter requires evacuation. Fluctuating grid with breach attempt needs human dispatch. Stable grid with secure perimeter needs reporting.",
  ),

  "Border Security": makeExercise(
    "Border Security",
    { domain: "Sensors", label: "Border Sensors", description: "Border surveillance and ground sensor data", states: [
      { value: "clear", label: "Clear", description: "No border activity detected" },
      { value: "activity", label: "Activity", description: "Suspicious activity detected — investigate" },
      { value: "breach", label: "Breach", description: "Border breach in progress" },
    ]},
    { domain: "Intel", label: "Intelligence", description: "Intelligence and threat assessment data", states: [
      { value: "none", label: "No Intel", description: "No threat intelligence" },
      { value: "advisory", label: "Advisory", description: "Threat advisory — heightened alert" },
      { value: "imminent", label: "Imminent", description: "Imminent threat — respond now" },
    ]},
    "Border breach with imminent threat requires human dispatch (patrol response). Activity with advisory needs SMS alert. Clear conditions need reporting.",
  ),

  "Hospitality": makeExercise(
    "Hospitality",
    { domain: "Fire", label: "Fire Safety", description: "Hotel fire and smoke detection systems", states: [
      { value: "clear", label: "Clear", description: "No fire indicators" },
      { value: "smoke", label: "Smoke", description: "Smoke detected — investigate" },
      { value: "active", label: "Active Fire", description: "Fire confirmed — evacuate" },
    ]},
    { domain: "Security", label: "Guest Safety", description: "Property security and guest safety sensors", states: [
      { value: "secure", label: "Secure", description: "All property security nominal" },
      { value: "alert", label: "Alert", description: "Security alert — dispatch staff" },
      { value: "threat", label: "Active Threat", description: "Active threat to guest safety" },
    ]},
    "Active fire with active threat requires evacuation. Smoke with security alert needs human dispatch. Clear conditions need reporting.",
  ),

  "HR Management": makeExercise(
    "HR Management",
    { domain: "Workplace", label: "Workplace Hazards", description: "Workplace environmental and safety sensors", states: [
      { value: "safe", label: "Safe", description: "No workplace hazards detected" },
      { value: "caution", label: "Caution", description: "Minor hazards — address" },
      { value: "hazard", label: "Hazard", description: "Serious hazard — stop work" },
    ]},
    { domain: "Compliance", label: "Compliance Status", description: "Regulatory compliance monitoring data", states: [
      { value: "compliant", label: "Compliant", description: "All compliance requirements met" },
      { value: "gap", label: "Gap", description: "Compliance gap detected — correct" },
      { value: "violation", label: "Violation", description: "Compliance violation — report" },
    ]},
    "Serious hazard with compliance violation requires hardware shutdown (stop work). Caution with compliance gap needs human dispatch. Safe conditions need reporting.",
  ),

  "Supplier Management": makeExercise(
    "Supplier Management",
    { domain: "Supplier Safety", label: "Supplier Safety Score", description: "Supplier safety performance metrics", states: [
      { value: "passing", label: "Passing", description: "Supplier safety metrics within threshold" },
      { value: "declining", label: "Declining", description: "Supplier safety declining — review" },
      { value: "fail", label: "Failing", description: "Supplier safety failing — reroute" },
    ]},
    { domain: "Supply Chain", label: "Delivery Status", description: "Supply chain delivery and logistics data", states: [
      { value: "on_time", label: "On Time", description: "Deliveries on schedule" },
      { value: "delayed", label: "Delayed", description: "Deliveries delayed — impact" },
      { value: "disrupted", label: "Disrupted", description: "Supply chain disrupted — alternate" },
    ]},
    "Failing supplier safety with disrupted supply chain requires hardware intervention (reroute supply). Declining safety with delayed deliveries needs human dispatch. Passing safety with on-time delivery needs reporting.",
  ),

  "Finance": makeExercise(
    "Finance",
    { domain: "Fraud", label: "Fraud Detection", description: "Transaction fraud detection system data", states: [
      { value: "clean", label: "Clean", description: "No fraud indicators" },
      { value: "suspicious", label: "Suspicious", description: "Suspicious transaction pattern" },
      { value: "confirmed", label: "Confirmed Fraud", description: "Fraud confirmed — block" },
    ]},
    { domain: "Systems", label: "System Health", description: "Core banking system health sensors", states: [
      { value: "operational", label: "Operational", description: "All systems operational" },
      { value: "degraded", label: "Degraded", description: "System degradation — investigate" },
      { value: "fail", label: "Failure", description: "System failure — failover" },
    ]},
    "Confirmed fraud with system failure requires hardware intervention (system failover). Suspicious with degraded needs human dispatch. Clean with operational needs reporting.",
  ),

  "Retail": makeExercise(
    "Retail",
    { domain: "Loss Prevention", label: "Theft Detection", description: "Loss prevention and inventory sensors", states: [
      { value: "normal", label: "Normal", description: "No theft indicators" },
      { value: "suspicious", label: "Suspicious", description: "Suspicious activity — monitor" },
      { value: "theft", label: "Active Theft", description: "Theft in progress — respond" },
    ]},
    { domain: "Equipment", label: "Equipment Health", description: "Refrigeration and HVAC sensor data", states: [
      { value: "operational", label: "Operational", description: "All equipment operating normally" },
      { value: "warning", label: "Warning", description: "Equipment issues — maintenance" },
      { value: "fail", label: "Failure", description: "Equipment failure — product loss risk" },
    ]},
    "Active theft with equipment failure requires human dispatch. Suspicious activity with warning equipment needs SMS alert. Normal conditions need reporting.",
  ),

  "Insurance": makeExercise(
    "Insurance",
    { domain: "Asset Risk", label: "Asset Risk Score", description: "Insured asset risk monitoring sensors", states: [
      { value: "low", label: "Low Risk", description: "Asset risk within acceptable range" },
      { value: "moderate", label: "Moderate", description: "Risk increasing — advisory" },
      { value: "high", label: "High Risk", description: "High risk — loss imminent" },
    ]},
    { domain: "Loss", label: "Loss Indicators", description: "Loss event detection and claims data", states: [
      { value: "none", label: "None", description: "No loss indicators" },
      { value: "minor", label: "Minor", description: "Minor loss event detected" },
      { value: "major", label: "Major", description: "Major loss event — claims likely" },
    ]},
    "High risk with major loss requires human dispatch (claims team). Moderate risk with minor loss needs SMS alert. Low risk with no loss needs reporting.",
  ),

  "Robotics": makeExercise(
    "Robotics",
    { domain: "Motion", label: "Collision Risk", description: "Robot motion and proximity sensor data", states: [
      { value: "clear", label: "Clear", description: "No collision risk detected" },
      { value: "approaching", label: "Approaching", description: "Obstacle approaching — slow" },
      { value: "imminent", label: "Imminent", description: "Collision imminent — stop" },
    ]},
    { domain: "Joints", label: "Joint Health", description: "Robot joint wear and temperature sensors", states: [
      { value: "healthy", label: "Healthy", description: "All joints within spec" },
      { value: "worn", label: "Worn", description: "Joint wear detected — service" },
      { value: "fail", label: "Failure", description: "Joint failure — shutdown" },
    ]},
    "Imminent collision with joint failure requires hardware shutdown. Approaching obstacle with worn joints needs human dispatch. Clear conditions need reporting.",
  ),

  "AI Systems": makeExercise(
    "AI Systems",
    { domain: "Model", label: "Model Performance", description: "AI model accuracy and performance metrics", states: [
      { value: "optimal", label: "Optimal", description: "Model performing within spec" },
      { value: "drifting", label: "Drifting", description: "Model drift detected — retrain" },
      { value: "fail", label: "Failure", description: "Model failure — rollback" },
    ]},
    { domain: "Input", label: "Input Validation", description: "Input anomaly and adversarial detection", states: [
      { value: "clean", label: "Clean", description: "Inputs within expected distribution" },
      { value: "anomalous", label: "Anomalous", description: "Anomalous inputs — filter" },
      { value: "adversarial", label: "Adversarial", description: "Adversarial attack detected — block" },
    ]},
    "Model failure with adversarial input requires software intervention (model rollback). Drifting with anomalous inputs needs human dispatch. Optimal with clean inputs needs reporting.",
  ),

  "Data Centers": makeExercise(
    "Data Centers",
    { domain: "Thermal", label: "Temperature", description: "Data center temperature sensors", states: [
      { value: "optimal", label: "Optimal", description: "Temperature within cooling capacity" },
      { value: "elevated", label: "Elevated", description: "Temperature rising — adjust cooling" },
      { value: "critical", label: "Critical", description: "Thermal excursion — equipment damage" },
    ]},
    { domain: "Power", label: "Power System", description: "Power distribution and UPS sensors", states: [
      { value: "stable", label: "Stable", description: "Power systems stable" },
      { value: "fluctuating", label: "Fluctuating", description: "Power fluctuations — investigate" },
      { value: "fail", label: "Failure", description: "Power failure — failover" },
    ]},
    "Critical thermal with power failure requires hardware intervention (failover). Elevated thermal with fluctuating power needs human dispatch. Optimal with stable power needs reporting.",
  ),

  "Semiconductor": makeExercise(
    "Semiconductor",
    { domain: "Tool", label: "Tool Health", description: "Fab tool degradation and performance sensors", states: [
      { value: "optimal", label: "Optimal", description: "All tools performing within spec" },
      { value: "degraded", label: "Degraded", description: "Tool degradation — schedule maintenance" },
      { value: "fail", label: "Failure", description: "Tool failure — wafer damage risk" },
    ]},
    { domain: "Cleanroom", label: "Contamination", description: "Cleanroom particle and contamination sensors", states: [
      { value: "clean", label: "Clean", description: "Cleanroom within spec" },
      { value: "elevated", label: "Elevated", description: "Particle count elevated — investigate" },
      { value: "contaminated", label: "Contaminated", description: "Contamination event — isolate" },
    ]},
    "Tool failure with contamination requires hardware shutdown. Degraded tools with elevated particles needs human dispatch. Optimal conditions need reporting.",
  ),

  "Culinary": makeExercise(
    "Culinary",
    { domain: "Temperature", label: "Food Temperature", description: "Kitchen food storage and holding temperature sensors", states: [
      { value: "safe", label: "Safe", description: "All temperatures within food safety range" },
      { value: "deviation", label: "Deviation", description: "Temperature deviating from safe range" },
      { value: "danger", label: "Danger Zone", description: "Temperature in danger zone — bacteria risk" },
    ]},
    { domain: "Hygiene", label: "Hygiene Monitoring", description: "Surface and equipment hygiene sensor data", states: [
      { value: "clean", label: "Clean", description: "All hygiene checks passing" },
      { value: "caution", label: "Caution", description: "Hygiene issues detected — address" },
      { value: "fail", label: "Failed", description: "Hygiene failure — health violation" },
    ]},
    "Danger zone temperature with hygiene failure requires hardware shutdown (stop service). Deviation with caution needs human dispatch. Safe with clean needs reporting.",
  ),

  "Education": makeExercise(
    "Education",
    { domain: "Campus Security", label: "Access Control", description: "Campus door and gate access sensors", states: [
      { value: "secure", label: "Secure", description: "All access points secured" },
      { value: "alert", label: "Alert", description: "Unauthorized access attempt" },
      { value: "breach", label: "Breach", description: "Security breach — lockdown" },
    ]},
    { domain: "Environmental", label: "Air & Fire", description: "Air quality and fire detection sensors", states: [
      { value: "normal", label: "Normal", description: "Environmental sensors nominal" },
      { value: "warning", label: "Warning", description: "Environmental hazard developing" },
      { value: "critical", label: "Critical", description: "Critical environmental threat" },
    ]},
    "Security breach with critical environmental threat requires evacuation. Alert with warning needs human dispatch. Secure with normal needs reporting.",
  ),

  "Law Enforcement": makeExercise(
    "Law Enforcement",
    { domain: "Officer Safety", label: "Officer Sensors", description: "Officer-worn vital and environmental sensors", states: [
      { value: "safe", label: "Safe", description: "Officer vitals and environment normal" },
      { value: "stress", label: "Stress", description: "Officer stress indicators — backup" },
      { value: "critical", label: "Critical", description: "Officer in distress — emergency" },
    ]},
    { domain: "Threat", label: "Threat Assessment", description: "Multi-domain threat detection and scoring", states: [
      { value: "none", label: "No Threat", description: "No threats detected" },
      { value: "emerging", label: "Emerging", description: "Emerging threat — assess" },
      { value: "active", label: "Active", description: "Active threat — engage" },
    ]},
    "Critical officer status with active threat requires human dispatch (emergency backup). Stress with emerging threat needs SMS alert. Safe with no threat needs reporting.",
  ),

  "Healthcare": makeExercise(
    "Healthcare",
    { domain: "Patient Vitals", label: "Patient Monitoring", description: "Multi-vital patient monitoring sensors", states: [
      { value: "stable", label: "Stable", description: "Patient vitals stable" },
      { value: "declining", label: "Declining", description: "Vital signs trending down — alert" },
      { value: "critical", label: "Critical", description: "Patient critical — code blue" },
    ]},
    { domain: "Environment", label: "Facility Safety", description: "Hospital environmental and safety sensors", states: [
      { value: "normal", label: "Normal", description: "Facility environment normal" },
      { value: "warning", label: "Warning", description: "Environmental hazard developing" },
      { value: "critical", label: "Critical", description: "Facility safety critical" },
    ]},
    "Critical patient vitals with critical facility safety requires evacuation. Declining vitals with warning environment needs human dispatch. Stable with normal needs reporting.",
  ),

  "Nonprofits": makeExercise(
    "Nonprofits",
    { domain: "Facility", label: "Facility Safety", description: "Nonprofit facility safety sensors", states: [
      { value: "safe", label: "Safe", description: "Facility safety systems nominal" },
      { value: "caution", label: "Caution", description: "Safety concerns — address" },
      { value: "hazard", label: "Hazard", description: "Safety hazard — evacuate" },
    ]},
    { domain: "Operations", label: "Operations Status", description: "Operational continuity and service data", states: [
      { value: "normal", label: "Normal", description: "Operations running normally" },
      { value: "disrupted", label: "Disrupted", description: "Operations disrupted — recover" },
      { value: "fail", label: "Failure", description: "Operational failure — activate plan" },
    ]},
    "Safety hazard with operational failure requires evacuation. Caution with disrupted operations needs human dispatch. Safe with normal needs reporting.",
  ),

  "Transportation": makeExercise(
    "Transportation",
    { domain: "Fleet", label: "Fleet Health", description: "Vehicle fleet diagnostic sensors", states: [
      { value: "healthy", label: "Healthy", description: "All fleet vehicles healthy" },
      { value: "warning", label: "Warning", description: "Vehicle issues — maintenance" },
      { value: "fail", label: "Failure", description: "Vehicle failure — safety risk" },
    ]},
    { domain: "Route", label: "Route Safety", description: "Route and infrastructure condition sensors", states: [
      { value: "clear", label: "Clear", description: "Routes clear and safe" },
      { value: "hazard", label: "Hazard", description: "Route hazards — reroute" },
      { value: "blocked", label: "Blocked", description: "Route blocked — alternate" },
    ]},
    "Vehicle failure with blocked route requires hardware shutdown. Warning with hazard needs human dispatch. Healthy with clear needs reporting.",
  ),
};

export function getTruthTableExercise(industry: string): TruthTableExercise | undefined {
  return TRUTH_TABLE_EXERCISES[industry];
}
